import type { EventHandler, EventMap, Unsubscribe } from './types';

/**
 * Generic typed publish/subscribe event bus.
 *
 * @typeParam TEventMap - Map of event types to their payload types
 *
 * @example
 * ```typescript
 * type MyEvents = {
 *   'user.created': { id: string; name: string };
 *   'user.deleted': { id: string };
 * };
 *
 * const bus = new EventBus<MyEvents>();
 *
 * const unsubscribe = bus.subscribe('user.created', (payload) => {
 *   console.log('User created:', payload.name);
 * });
 *
 * bus.publish('user.created', { id: '123', name: 'Alice' });
 *
 * unsubscribe();
 * ```
 */
class EventBus<TEventMap extends EventMap = EventMap> {
  private readonly handlers = new Map<keyof TEventMap, Set<EventHandler<unknown>>>();

  /**
   * Subscribe to an event type.
   *
   * @param eventType - The event type to subscribe to
   * @param handler - Callback invoked when event is published
   * @returns Unsubscribe function to remove the handler
   */
  public subscribe<K extends keyof TEventMap>(
    eventType: K,
    handler: EventHandler<TEventMap[K]>,
  ): Unsubscribe {
    if (!this.handlers.has(eventType)) {
      this.handlers.set(eventType, new Set());
    }

    const handlers = this.handlers.get(eventType)!;
    handlers.add(handler as EventHandler<unknown>);

    return (): void => {
      handlers.delete(handler as EventHandler<unknown>);
      if (handlers.size === 0) {
        this.handlers.delete(eventType);
      }
    };
  }

  /**
   * Publish an event to all subscribers.
   *
   * @param eventType - The event type to publish
   * @param payload - The event payload
   */
  public publish<K extends keyof TEventMap>(eventType: K, payload: TEventMap[K]): void {
    const handlers = this.handlers.get(eventType);
    if (handlers) {
      handlers.forEach((handler) => {
        handler(payload);
      });
    }
  }

  /**
   * Get the number of subscribers for an event type.
   *
   * @param eventType - The event type to check
   * @returns Number of subscribers
   */
  public subscriberCount<K extends keyof TEventMap>(eventType: K): number {
    return this.handlers.get(eventType)?.size ?? 0;
  }

  /**
   * Check if an event type has any subscribers.
   *
   * @param eventType - The event type to check
   * @returns True if there are subscribers
   */
  public hasSubscribers<K extends keyof TEventMap>(eventType: K): boolean {
    return this.subscriberCount(eventType) > 0;
  }

  /**
   * Remove all subscribers for all event types.
   */
  public clear(): void {
    this.handlers.clear();
  }
}

export default EventBus;
